import { createTheme, alpha, Theme } from '@mui/material/styles';

/* ─────────────────────────────────────────────────────────────
   Design system: minimal, sharp, developer-focused
   Inspired by Linear, Vercel, Cursor
   ───────────────────────────────────────────────────────────── */

const BRAND  = '#FF1A50';
const BRAND_D = '#E0143F';

const typography = {
  fontFamily: '"Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
  h1: { fontWeight: 700, letterSpacing: '-0.04em', lineHeight: 1.1 },
  h2: { fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.15 },
  h3: { fontWeight: 600, letterSpacing: '-0.025em', lineHeight: 1.2 },
  h4: { fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.25 },
  h5: { fontWeight: 600, letterSpacing: '-0.015em' },
  h6: { fontWeight: 600, letterSpacing: '-0.01em' },
  button: { textTransform: 'none' as const, fontWeight: 500, letterSpacing: '-0.01em' },
  body1: { letterSpacing: '-0.01em', lineHeight: 1.6 },
  body2: { letterSpacing: '-0.005em', lineHeight: 1.5 },
};

/* ─── DARK ─── */
export const darkTheme: Theme = createTheme({
  palette: {
    mode: 'dark',
    primary:    { main: BRAND, dark: BRAND_D, light: '#FF4D7A', contrastText: '#fff' },
    secondary:  { main: '#888888', contrastText: '#fff' },
    background: { default: '#0A0A0A', paper: '#111111' },
    text:       { primary: '#EFEFEF', secondary: '#888888', disabled: '#444444' },
    divider:    'rgba(255,255,255,0.07)',
    success:    { main: '#22C55E', dark: '#16A34A', light: '#4ADE80', contrastText: '#000' },
    error:      { main: '#EF4444', dark: '#DC2626', light: '#F87171', contrastText: '#fff' },
    warning:    { main: '#F59E0B', dark: '#D97706', light: '#FCD34D', contrastText: '#000' },
    info:       { main: '#3B82F6', dark: '#2563EB', light: '#60A5FA', contrastText: '#fff' },
    action: {
      hover: 'rgba(255,255,255,0.04)',
      selected: 'rgba(255,255,255,0.06)',
    },
  },
  typography,
  shape: { borderRadius: 8 },
  shadows: [
    'none',
    '0 1px 2px rgba(0,0,0,0.4)',
    '0 2px 4px rgba(0,0,0,0.4)',
    '0 4px 8px rgba(0,0,0,0.4)',
    '0 8px 16px rgba(0,0,0,0.4)',
    '0 16px 32px rgba(0,0,0,0.4)',
    '0 20px 40px rgba(0,0,0,0.4)',
    '0 24px 48px rgba(0,0,0,0.4)',
    '0 1px 2px rgba(0,0,0,0.5)',
    '0 2px 4px rgba(0,0,0,0.5)',
    '0 4px 8px rgba(0,0,0,0.5)',
    '0 8px 16px rgba(0,0,0,0.5)',
    '0 16px 32px rgba(0,0,0,0.5)',
    '0 24px 48px rgba(0,0,0,0.5)',
    '0 32px 64px rgba(0,0,0,0.5)',
    '0 1px 3px rgba(0,0,0,0.6)',
    '0 2px 6px rgba(0,0,0,0.6)',
    '0 4px 12px rgba(0,0,0,0.6)',
    '0 8px 24px rgba(0,0,0,0.6)',
    '0 16px 48px rgba(0,0,0,0.6)',
    '0 24px 64px rgba(0,0,0,0.6)',
    '0 32px 80px rgba(0,0,0,0.6)',
    '0 40px 96px rgba(0,0,0,0.6)',
    '0 48px 112px rgba(0,0,0,0.6)',
    '0 56px 128px rgba(0,0,0,0.6)',
  ] as any,
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400&family=JetBrains+Mono:wght@400;500&display=swap');

        *, *::before, *::after { box-sizing: border-box; }
        html { scroll-behavior: smooth; }

        body {
          background: #0A0A0A;
          min-height: 100vh;
          overflow-x: hidden;
        }

        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 999px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }

        ::selection { background: rgba(255,26,80,0.3); color: #fff; }
      `,
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 7,
          padding: '8px 16px',
          fontSize: '0.875rem',
          fontWeight: 500,
          transition: 'all 0.15s ease',
          letterSpacing: '-0.01em',
        },
        contained: {
          background: BRAND,
          boxShadow: 'none',
          '&:hover': { background: BRAND_D, boxShadow: 'none', transform: 'none' },
          '&:active': { background: '#C0102F', transform: 'scale(0.98)' },
        },
        outlined: {
          borderColor: 'rgba(255,255,255,0.12)',
          color: '#EFEFEF',
          backgroundColor: 'transparent',
          '&:hover': { borderColor: 'rgba(255,255,255,0.25)', backgroundColor: 'rgba(255,255,255,0.04)' },
        },
        text: {
          color: '#888888',
          '&:hover': { color: '#EFEFEF', backgroundColor: 'rgba(255,255,255,0.04)' },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#111111',
          border: '1px solid rgba(255,255,255,0.07)',
          boxShadow: 'none',
          '&::before': { display: 'none' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'border-color 0.15s ease',
          '&:hover': { transform: 'none', borderColor: 'rgba(255,255,255,0.12)' },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: '#111111',
            fontWeight: 500,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: '#555555',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            padding: '10px 16px',
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            transition: 'background 0.1s ease',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.025)' },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          padding: '12px 16px',
          fontSize: '0.875rem',
          color: '#EFEFEF',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: '0.72rem',
          borderRadius: 5,
          height: 22,
          letterSpacing: '0.01em',
          border: 'none',
        },
      },
    },
    MuiTextField: { defaultProps: { variant: 'outlined' as const, size: 'small' as const } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 7,
          fontSize: '0.875rem',
          backgroundColor: '#0A0A0A',
          transition: 'all 0.15s ease',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND, borderWidth: 1 },
          '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(BRAND, 0.15)}` },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: '0.875rem', color: '#555555', '&.Mui-focused': { color: '#888888' } },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: { fontSize: '0.875rem' },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          padding: '8px 12px',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
          '&.Mui-selected': { backgroundColor: 'rgba(255,26,80,0.1)', '&:hover': { backgroundColor: 'rgba(255,26,80,0.15)' } },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
          border: '1px solid rgba(255,255,255,0.08)',
          backgroundColor: '#111111',
          backgroundImage: 'none',
          '&::before': { display: 'none' },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          fontWeight: 600,
          color: '#EFEFEF',
          padding: '20px 24px 12px',
          letterSpacing: '-0.01em',
        },
      },
    },
    MuiDialogContent: { styleOverrides: { root: { padding: '8px 24px 20px' } } },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: '12px 24px 20px', gap: 8, borderTop: '1px solid rgba(255,255,255,0.07)' },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: 'rgba(255,255,255,0.07)', '&::before, &::after': { borderColor: 'rgba(255,255,255,0.07)' } },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 6,
          fontSize: '0.75rem',
          fontWeight: 500,
          backgroundColor: '#1A1A1A',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          padding: '6px 10px',
          color: '#EFEFEF',
        },
        arrow: { color: '#1A1A1A' },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 7,
          transition: 'all 0.15s ease',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, height: 4, backgroundColor: 'rgba(255,255,255,0.06)' },
        bar: { borderRadius: 4, backgroundColor: BRAND },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND, '& + .MuiSwitch-track': { backgroundColor: alpha(BRAND, 0.6), opacity: 1 } },
          '& .MuiSwitch-track': { borderRadius: 999, backgroundColor: 'rgba(255,255,255,0.15)' },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { fontWeight: 500, fontSize: '0.875rem', textTransform: 'none', letterSpacing: '-0.01em', '&.Mui-selected': { color: '#EFEFEF' } },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 1, backgroundColor: '#EFEFEF' },
      },
    },
    MuiAvatar: {
      styleOverrides: { root: { border: '1px solid rgba(255,255,255,0.1)' } },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8, fontSize: '0.875rem' },
        standard: {
          '&.MuiAlert-standardSuccess': { backgroundColor: alpha('#22C55E', 0.1), border: `1px solid ${alpha('#22C55E', 0.2)}`, color: '#4ADE80' },
          '&.MuiAlert-standardError': { backgroundColor: alpha('#EF4444', 0.1), border: `1px solid ${alpha('#EF4444', 0.2)}`, color: '#F87171' },
          '&.MuiAlert-standardWarning': { backgroundColor: alpha('#F59E0B', 0.1), border: `1px solid ${alpha('#F59E0B', 0.2)}`, color: '#FCD34D' },
          '&.MuiAlert-standardInfo': { backgroundColor: alpha('#3B82F6', 0.1), border: `1px solid ${alpha('#3B82F6', 0.2)}`, color: '#60A5FA' },
        },
        filled: {
          '&.MuiAlert-filledSuccess': { backgroundColor: '#16A34A' },
          '&.MuiAlert-filledError': { backgroundColor: '#DC2626' },
          '&.MuiAlert-filledWarning': { backgroundColor: '#D97706', color: '#000' },
          '&.MuiAlert-filledInfo': { backgroundColor: '#2563EB' },
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: { backgroundColor: 'rgba(255,255,255,0.04)', transform: 'none', borderRadius: 4 },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' },
        option: { fontSize: '0.875rem', padding: '8px 12px', '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' } },
        noOptions: { fontSize: '0.875rem', color: '#555555', padding: '12px 16px' },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: { fontSize: '0.875rem', color: '#888888' },
      },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { fontSize: '0.75rem', color: '#555555', marginTop: 4 } },
    },
  },
});

/* ─── LIGHT ─── */
export const lightTheme: Theme = createTheme({
  palette: {
    mode: 'light',
    primary:    { main: BRAND, dark: BRAND_D, light: '#FF4D7A', contrastText: '#fff' },
    secondary:  { main: '#666666', contrastText: '#fff' },
    background: { default: '#F5F5F5', paper: '#FFFFFF' },
    text:       { primary: '#0A0A0A', secondary: '#666666', disabled: '#AAAAAA' },
    divider:    'rgba(0,0,0,0.07)',
    success:    { main: '#16A34A', dark: '#15803D', light: '#22C55E', contrastText: '#fff' },
    error:      { main: '#DC2626', dark: '#B91C1C', light: '#EF4444', contrastText: '#fff' },
    warning:    { main: '#D97706', dark: '#B45309', light: '#F59E0B', contrastText: '#fff' },
    info:       { main: '#2563EB', dark: '#1D4ED8', light: '#3B82F6', contrastText: '#fff' },
    action: {
      hover: 'rgba(0,0,0,0.03)',
      selected: 'rgba(0,0,0,0.05)',
    },
  },
  typography,
  shape: { borderRadius: 8 },
  shadows: [
    'none',
    '0 1px 2px rgba(0,0,0,0.06)',
    '0 2px 4px rgba(0,0,0,0.07)',
    '0 4px 8px rgba(0,0,0,0.07)',
    '0 8px 16px rgba(0,0,0,0.08)',
    '0 16px 32px rgba(0,0,0,0.08)',
    '0 20px 40px rgba(0,0,0,0.08)',
    '0 24px 48px rgba(0,0,0,0.1)',
    '0 1px 3px rgba(0,0,0,0.08)',
    '0 2px 6px rgba(0,0,0,0.08)',
    '0 4px 12px rgba(0,0,0,0.08)',
    '0 8px 24px rgba(0,0,0,0.08)',
    '0 16px 40px rgba(0,0,0,0.1)',
    '0 24px 56px rgba(0,0,0,0.1)',
    '0 32px 72px rgba(0,0,0,0.1)',
    '0 1px 4px rgba(0,0,0,0.1)',
    '0 2px 8px rgba(0,0,0,0.1)',
    '0 4px 16px rgba(0,0,0,0.1)',
    '0 8px 32px rgba(0,0,0,0.1)',
    '0 16px 64px rgba(0,0,0,0.1)',
    '0 24px 80px rgba(0,0,0,0.1)',
    '0 32px 96px rgba(0,0,0,0.1)',
    '0 40px 112px rgba(0,0,0,0.12)',
    '0 48px 128px rgba(0,0,0,0.12)',
    '0 56px 144px rgba(0,0,0,0.12)',
  ] as any,
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400&family=JetBrains+Mono:wght@400;500&display=swap');

        *, *::before, *::after { box-sizing: border-box; }
        html { scroll-behavior: smooth; }

        body {
          background: #F5F5F5;
          min-height: 100vh;
          overflow-x: hidden;
        }

        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 999px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.25); }

        ::selection { background: rgba(255,26,80,0.2); color: #0A0A0A; }
      `,
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 7,
          padding: '8px 16px',
          fontSize: '0.875rem',
          fontWeight: 500,
          transition: 'all 0.15s ease',
          letterSpacing: '-0.01em',
        },
        contained: {
          background: BRAND,
          boxShadow: 'none',
          '&:hover': { background: BRAND_D, boxShadow: 'none', transform: 'none' },
          '&:active': { background: '#C0102F', transform: 'scale(0.98)' },
        },
        outlined: {
          borderColor: 'rgba(0,0,0,0.15)',
          color: '#0A0A0A',
          backgroundColor: 'transparent',
          '&:hover': { borderColor: 'rgba(0,0,0,0.3)', backgroundColor: 'rgba(0,0,0,0.03)' },
        },
        text: {
          color: '#666666',
          '&:hover': { color: '#0A0A0A', backgroundColor: 'rgba(0,0,0,0.04)' },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#FFFFFF',
          border: '1px solid rgba(0,0,0,0.07)',
          boxShadow: 'none',
          '&::before': { display: 'none' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'border-color 0.15s ease',
          '&:hover': { transform: 'none', borderColor: 'rgba(0,0,0,0.14)' },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: '#FFFFFF',
            fontWeight: 500,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: '#999999',
            borderBottom: '1px solid rgba(0,0,0,0.07)',
            padding: '10px 16px',
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            transition: 'background 0.1s ease',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.015)' },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(0,0,0,0.05)',
          padding: '12px 16px',
          fontSize: '0.875rem',
          color: '#0A0A0A',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500, fontSize: '0.72rem', borderRadius: 5, height: 22, letterSpacing: '0.01em', border: 'none' },
      },
    },
    MuiTextField: { defaultProps: { variant: 'outlined' as const, size: 'small' as const } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 7,
          fontSize: '0.875rem',
          backgroundColor: '#FFFFFF',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.14)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.25)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND, borderWidth: 1 },
          '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(BRAND, 0.12)}` },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: '0.875rem', color: '#999999', '&.Mui-focused': { color: '#666666' } },
      },
    },
    MuiSelect: { styleOverrides: { root: { fontSize: '0.875rem' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          padding: '8px 12px',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
          '&.Mui-selected': { backgroundColor: alpha(BRAND, 0.06), '&:hover': { backgroundColor: alpha(BRAND, 0.1) } },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          border: '1px solid rgba(0,0,0,0.08)',
          backgroundColor: '#FFFFFF',
          backgroundImage: 'none',
          '&::before': { display: 'none' },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontSize: '1rem', fontWeight: 600, color: '#0A0A0A', padding: '20px 24px 12px', letterSpacing: '-0.01em' },
      },
    },
    MuiDialogContent: { styleOverrides: { root: { padding: '8px 24px 20px' } } },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: '12px 24px 20px', gap: 8, borderTop: '1px solid rgba(0,0,0,0.07)' },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: 'rgba(0,0,0,0.07)', '&::before, &::after': { borderColor: 'rgba(0,0,0,0.07)' } },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 6, fontSize: '0.75rem', fontWeight: 500,
          backgroundColor: '#111111', color: '#EFEFEF',
          border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', padding: '6px 10px',
        },
        arrow: { color: '#111111' },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 7,
          transition: 'all 0.15s ease',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, height: 4, backgroundColor: 'rgba(0,0,0,0.06)' },
        bar: { borderRadius: 4, backgroundColor: BRAND },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND, '& + .MuiSwitch-track': { backgroundColor: alpha(BRAND, 0.5), opacity: 1 } },
          '& .MuiSwitch-track': { borderRadius: 999, backgroundColor: 'rgba(0,0,0,0.15)' },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { fontWeight: 500, fontSize: '0.875rem', textTransform: 'none', letterSpacing: '-0.01em', '&.Mui-selected': { color: '#0A0A0A' } },
      },
    },
    MuiTabs: {
      styleOverrides: { indicator: { height: 1, backgroundColor: '#0A0A0A' } },
    },
    MuiAvatar: {
      styleOverrides: { root: { border: '1px solid rgba(0,0,0,0.1)' } },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8, fontSize: '0.875rem' },
        standard: {
          '&.MuiAlert-standardSuccess': { backgroundColor: alpha('#16A34A', 0.08), border: `1px solid ${alpha('#16A34A', 0.2)}`, color: '#15803D' },
          '&.MuiAlert-standardError': { backgroundColor: alpha('#DC2626', 0.08), border: `1px solid ${alpha('#DC2626', 0.2)}`, color: '#DC2626' },
          '&.MuiAlert-standardWarning': { backgroundColor: alpha('#D97706', 0.08), border: `1px solid ${alpha('#D97706', 0.2)}`, color: '#D97706' },
          '&.MuiAlert-standardInfo': { backgroundColor: alpha('#2563EB', 0.08), border: `1px solid ${alpha('#2563EB', 0.2)}`, color: '#2563EB' },
        },
        filled: {
          '&.MuiAlert-filledSuccess': { backgroundColor: '#16A34A' },
          '&.MuiAlert-filledError': { backgroundColor: '#DC2626' },
          '&.MuiAlert-filledWarning': { backgroundColor: '#D97706', color: '#000' },
          '&.MuiAlert-filledInfo': { backgroundColor: '#2563EB' },
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: { backgroundColor: 'rgba(0,0,0,0.05)', transform: 'none', borderRadius: 4 },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { backgroundColor: '#FFFFFF', border: '1px solid rgba(0,0,0,0.1)', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' },
        option: { fontSize: '0.875rem', padding: '8px 12px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } },
        noOptions: { fontSize: '0.875rem', color: '#999999', padding: '12px 16px' },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: { label: { fontSize: '0.875rem', color: '#666666' } },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { fontSize: '0.75rem', color: '#999999', marginTop: 4 } },
    },
  },
});

export default darkTheme;
